@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.billing.newBillingRequestId
import com.royceecoleman.bill.billing.workbenchCall
import com.royceecoleman.bill.billing.workbenchJson
import com.royceecoleman.bill.settings.fetchManagedAiModels
import com.royceecoleman.bill.shared.aichat.ManagedAiModelDto
import com.royceecoleman.bill.shared.billing.AccountBillingView
import com.royceecoleman.bill.shared.billing.AccountDirectory
import com.royceecoleman.bill.shared.billing.AccountView
import com.royceecoleman.bill.shared.billing.AdminAction
import com.royceecoleman.bill.shared.billing.AuditItem
import com.royceecoleman.bill.shared.billing.CreditGrantRequest
import com.royceecoleman.bill.shared.billing.GrantRequest
import com.royceecoleman.bill.shared.billing.ModelPolicy
import com.royceecoleman.bill.shared.billing.OperationsOverview
import com.royceecoleman.bill.shared.billing.OperationsPolicy
import com.royceecoleman.bill.shared.billing.SupportCreate
import com.royceecoleman.bill.shared.billing.SupportList
import com.royceecoleman.bill.shared.billing.SupportReply
import com.royceecoleman.bill.shared.billing.SupportTicket
import com.royceecoleman.bill.shared.billing.QualityGroup
import com.royceecoleman.bill.shared.billing.UsageGroup
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.JsonObject
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import kotlin.js.Date

private fun stamp(value: Long): String =
    if (value ==
        0L
    ) {
        "Not recorded"
    } else {
        Date(value.toDouble()).toISOString().replace('T', ' ').take(19) + " UTC"
    }

private fun encodeFilter(value: String): String = js("encodeURIComponent(value)") as String

@Composable
internal fun OperationsPanel() {
    var tab by remember { mutableStateOf("Overview") }
    DisposableEffect(Unit) {
        val css = document.createElement("link")
        css.setAttribute("rel", "stylesheet")
        css.setAttribute("href", "/bill-workbench.css")
        document.head?.appendChild(css)
        onDispose { css.remove() }
    }
    Div({ classes("bill-app") }) {
        Header({ classes("bill-top") }) {
            H1 { Text("BILL operations") }
            A("/dashboard") { Text("Admin dashboard") }
            A("/") { Text("Workbench") }
        }
        Main({ classes("bill-main") }) {
            Nav({
                classes("bill-row")
                attr("aria-label", "Operations sections")
            }) {
                listOf("Overview", "Models", "Accounts", "Support", "Audit").forEach { name ->
                    BillButton(
                        name,
                        kind =
                            if (tab ==
                                name
                            ) {
                                "primary"
                            } else {
                                ""
                            },
                    ) { tab = name }
                }
            }
            when (tab) {
                "Overview" -> OperationsOverviewPanel()
                "Models" -> OperationsModelsPanel()
                "Accounts" -> OperationsAccountsPanel()
                "Support" -> SupportPanel(admin = true)
                "Audit" -> OperationsAuditPanel()
            }
        }
    }
}

@Composable
private fun OperationForm(content: @Composable (Boolean, ((suspend () -> Unit) -> Unit)) -> Unit) {
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    val run: (suspend () -> Unit) -> Unit = { action ->
        if (!busy) {
            busy = true
            error = ""
            scope.launch {
                try {
                    action()
                } catch (
                    cancelled: CancellationException,
                ) {
                    throw cancelled
                } catch (
                    failure: Exception,
                ) {
                    error = failure.message ?: "Request failed. Reload and retry."
                } finally {
                    busy = false
                }
            }
        }
    }
    CompositionLocalProvider(LocalWorkbenchBusy provides busy) {
        Div({ attr("aria-busy", busy.toString()) }) {
            if (error.isNotBlank()) {
                P({
                    classes("bill-alert")
                    attr("role", "alert")
                }) { Text(error) }
            }
            if (busy) P({ attr("role", "status") }) { Text("Working…") }
            content(busy, run)
        }
    }
}

@Composable
private fun OperationsOverviewPanel() {
    var data by remember { mutableStateOf<OperationsOverview?>(null) }
    var days by remember { mutableStateOf("30") }
    var owner by remember { mutableStateOf("") }
    var page by remember { mutableStateOf(0) }
    OperationForm { busy, run ->
        suspend fun refresh(next: Int = 0) {
            data =
                workbenchCall(
                    "/admin/overview?days=${encodeFilter(days)}&page=$next" + if (owner.isBlank()) "" else "&owner=${encodeFilter(owner)}",
                )
            page = next
        }
        LaunchedEffect(Unit) { run { refresh() } }
        H2 { Text("Costs & activity") }
        P {
            Text(
                "New workbench drafting, standing-rules chat and Ask BILL calls only. Provider-reported charges and estimates are separate; unknown costs are not free. No prompts, responses, or client names are stored in this ledger. Historical usage and the legacy AI-chat page are not included.",
            )
        }
        Div({ classes("bill-row") }) {
            BillField("Days (1–366)", days) { days = it }
            BillField("Account ID (blank = all)", owner) { owner = it }
            BillButton("Apply filters", busy) { run { refresh() } }
        }
        data?.let { report ->
            P {
                Text(
                    "Since ${report.from} UTC · ${report.activeAiUsers} active AI accounts · ${report.total.calls} calls · ${report.total.inputTokens} input / ${report.total.outputTokens} output tokens",
                )
            }
            H3 {
                Text(
                    "Reported USD ${report.total.reportedUsd} · Estimated USD ${report.total.estimatedUsd} · ${report.total.unknownCosts} unknown",
                )
            }
            UsageTable("By model", report.models)
            QualityTable(report.quality)
            BillDetails("Daily usage") { UsageTable("By day (UTC)", report.days) }
            H3 { Text("Workbench events (not unique-user conversions)") }
            if (report.activities.isEmpty()) P { Text("No events recorded in this period.") }
            report.activities.forEach { P { Text("${it.label.replace('_', ' ')}: ${it.count}") } }
            H3 { Text("AI call ledger — page ${page + 1}") }
            Div({ classes("bill-scroll") }) {
                Table({ classes("bill-list") }) {
                    Thead {
                        Tr {
                            listOf(
                                "Started (UTC)",
                                "Account",
                                "Task / model",
                                "Status",
                                "Tokens in / out",
                                "USD / basis",
                            ).forEach { Th { Text(it) } }
                        }
                    }
                    Tbody {
                        report.calls.forEach { call ->
                            Tr {
                                Td { Text(stamp(call.startedAt)) }
                                Td { Text(call.owner.toString()) }
                                Td { Text("${call.kind} / ${call.model}") }
                                Td { Text("${call.status} (${call.durationMs} ms)") }
                                Td {
                                    Text(
                                        "${call.inputTokens} / ${call.outputTokens}; cached ${call.cachedTokens}; reasoning ${call.reasoningTokens}",
                                    )
                                }
                                Td { Text("${call.costUsd.ifBlank { "unknown" }} / ${call.costBasis}") }
                            }
                        }
                    }
                }
            }
            BillButton("Previous calls", busy || page == 0) { run { refresh(page - 1) } }
            BillButton("Next calls", busy || !report.moreCalls) { run { refresh(page + 1) } }
        }
    }
}

/** How closely lawyers kept each model's drafts; a better model shows more unchanged approvals and higher similarity. */
@Composable
private fun QualityTable(rows: List<QualityGroup>) {
    H3 { Text("Draft quality by model (approvals in this period)") }
    if (rows.isEmpty()) {
        P { Text("No approved AI drafts in this period.") }
        return
    }
    Div({ classes("bill-scroll") }) {
        Table({ classes("bill-list") }) {
            Thead {
                Tr {
                    listOf("Model", "Approved drafts", "Approved unchanged", "Average wording kept", "Task codes kept", "Activity codes kept").forEach { Th { Text(it) } }
                }
            }
            Tbody {
                rows.forEach { row ->
                    fun percent(count: Long) = if (row.approvedDrafts == 0L) "—" else "${count * 100 / row.approvedDrafts}%"
                    Tr {
                        listOf(
                            row.label,
                            row.approvedDrafts.toString(),
                            percent(row.unchanged),
                            "${(row.meanSimilarity * 100).toInt()}%",
                            percent(row.taskCodeKept),
                            percent(row.activityCodeKept),
                        ).forEach { Td { Text(it) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun UsageTable(
    title: String,
    rows: List<UsageGroup>,
) {
    H3 { Text(title) }
    Div({ classes("bill-scroll") }) {
        Table({ classes("bill-list") }) {
            Thead {
                Tr {
                    listOf(
                        "Group",
                        "Calls",
                        "Input / output",
                        "Reported USD",
                        "Estimated USD",
                        "Unknown costs",
                    ).forEach { Th { Text(it) } }
                }
            }
            Tbody {
                rows.forEach { row ->
                    Tr {
                        listOf(
                            row.label,
                            row.calls.toString(),
                            "${row.inputTokens} / ${row.outputTokens}",
                            row.reportedUsd,
                            row.estimatedUsd,
                            row.unknownCosts.toString(),
                        ).forEach {
                            Td { Text(it) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun OperationsModelsPanel() {
    var policy by remember { mutableStateOf<OperationsPolicy?>(null) }
    var catalog by remember { mutableStateOf<List<ManagedAiModelDto>>(emptyList()) }
    var notice by remember { mutableStateOf("") }
    OperationForm { busy, run ->
        suspend fun reload() {
            catalog = fetchManagedAiModels().filterNot { it.deprecated }
            policy =
                workbenchCall<OperationsPolicy>("/admin/policy").let { p ->
                    p.copy(
                        models =
                            catalog.map { model ->
                                p.models.find {
                                    it.modelId ==
                                        model.id
                                }
                                    ?: ModelPolicy(model.id)
                            },
                    )
                }
        }
        LaunchedEffect(Unit) { run { reload() } }
        H2 { Text("Models & pricing policy") }
        P {
            Text(
                "Luna is the default for bounded drafting and guidance. Lawyers still review every entry. Use separate task overrides when evaluations justify a stronger model; no silent fallback is configured.",
            )
        }
        A("/settings") { Text("Open model catalog: add/edit deployments, choose global default, deprecate models") }
        P {
            Text(
                "Current global default: ${catalog
                    .find {
                        it.isDefault
                    }?.name ?: "Loading…"}. Reasoning/output controls below apply to every model (all models run through OpenRouter). Drafting models must support structured JSON outputs. Rates estimate calls without a provider-reported charge and never reprice historical calls.",
            )
        }
        policy?.let { p ->
            val options = mapOf("" to "Follow global default") + catalog.associate { it.id to it.name }
            BillSelect("Draft transformation model", p.draftModel, options) { policy = policy?.copy(draftModel = it) }
            BillSelect("Ask BILL assistant model", p.guideModel, options) { policy = policy?.copy(guideModel = it) }
            BillSelect(
                "Rules check model (reviews each batch after drafting)",
                p.reviewModel,
                mapOf("" to "Same as the draft model") + catalog.associate { it.id to it.name },
            ) { policy = policy?.copy(reviewModel = it) }
            p.models.forEach { model ->
                BillDetails(catalog.find { it.id == model.modelId }?.name ?: model.modelId) {
                    fun change(value: ModelPolicy) {
                        policy =
                            policy?.let { it.copy(models = it.models.map { old -> if (old.modelId == value.modelId) value else old }) }
                    }
                    if (catalog.find { it.id == model.modelId }?.provider == "OPENROUTER") {
                        BillSelect(
                            "${model.modelId} reasoning",
                            model.reasoning,
                            listOf(
                                "none",
                                "low",
                                "medium",
                                "high",
                                "xhigh",
                                "max",
                            ).associateWith {
                                it
                            },
                        ) { change(model.copy(reasoning = it)) }
                        BillField("${model.modelId} output token cap", model.maxOutputTokens.toString()) {
                            change(
                                model.copy(
                                    maxOutputTokens =
                                        it.toIntOrNull() ?: 0,
                                ),
                            )
                        }
                    }
                    BillField("${model.modelId} input USD / million tokens (blank = unknown)", model.inputUsdPerMillion) {
                        change(model.copy(inputUsdPerMillion = it))
                    }
                    BillField("${model.modelId} output USD / million tokens (blank = unknown)", model.outputUsdPerMillion) {
                        change(model.copy(outputUsdPerMillion = it))
                    }
                }
            }
            BillField("Policy change reason", p.reason) { policy = policy?.copy(reason = it) }
            BillButton("Save model policy", busy || p.reason.trim().length < 3, "primary") {
                if (window.confirm("Apply this policy to subsequent BILL AI requests?")) {
                    run {
                        policy = workbenchCall("/admin/policy", "PUT", workbenchJson.encodeToString(p))
                        notice =
                            "Policy saved. New requests use these settings."
                    }
                }
            }
        }
        BillButton("Reload model policy", busy) {
            run {
                reload()
                notice = ""
            }
        }
        if (notice.isNotBlank()) P({ attr("role", "status") }) { Text(notice) }
    }
}

@Composable
private fun OperationsAccountsPanel() {
    var directory by remember { mutableStateOf<AccountDirectory?>(null) }
    var search by remember { mutableStateOf("") }
    var page by remember { mutableStateOf(0) }
    var selected by remember { mutableStateOf<AccountView?>(null) }
    var billing by remember { mutableStateOf<AccountBillingView?>(null) }
    var reason by remember { mutableStateOf("") }
    var months by remember { mutableStateOf("1") }
    var password by remember { mutableStateOf("") }
    var grantId by remember { mutableStateOf(newBillingRequestId()) }
    var credits by remember { mutableStateOf("") }
    var creditGrantId by remember { mutableStateOf(newBillingRequestId()) }
    var notice by remember { mutableStateOf("") }
    OperationForm { busy, run ->
        suspend fun reload(next: Int = 0) {
            directory = workbenchCall("/admin/accounts?search=${encodeFilter(search)}&page=$next")
            page =
                next
        }
        LaunchedEffect(Unit) { run { reload() } }
        H2 { Text("Customer accounts") }
        A("/account-management") { Text("Create an account") }
        directory?.let { d ->
            P {
                Text(
                    "${d.totalCustomers} customers · ${d.newCustomers30d} registered in 30 days · ${d.activeCustomers30d} active in 30 days. Activity means an authenticated request; last-seen updates at most every five minutes. Legacy registration dates are unknown.",
                )
            }
        }
        BillField("Search usernames", search) { search = it }
        BillButton("Search accounts", busy) { run { reload() } }
        directory?.users?.forEach { user ->
            Div({ classes("bill-card") }) {
                P {
                    Text(
                        "#${user.id} ${user.username} · ${user.role} · ${if (user.locked) "LOCKED" else "Enabled"} · last seen ${stamp(
                            user.lastSeenAt,
                        )}",
                    )
                }
                BillButton("Manage ${user.username}", busy || user.role == "DEV") {
                    run {
                        selected = user
                        billing = null
                        reason = ""
                        password = ""
                        notice = ""
                        grantId = newBillingRequestId()
                        credits = ""
                        creditGrantId = newBillingRequestId()
                        if (user.role == "CUSTOMER") billing = workbenchCall("/admin/billing/${user.id}")
                    }
                }
            }
        }
        BillButton("Previous accounts", busy || page == 0) { run { reload(page - 1) } }
        BillButton("Next accounts", busy || directory?.more != true) { run { reload(page + 1) } }
        selected?.let { user ->
            Div({ classes("bill-card") }) {
                H3 { Text("Manage #${user.id} ${user.username}") }
                P {
                    Text(
                        "Account controls invalidate existing sessions immediately. Admins manage customers; developers may also manage other admins. Developer accounts and your own account are protected.",
                    )
                }
                BillField("Account action reason", reason) {
                    reason = it
                    grantId = newBillingRequestId()
                    creditGrantId = newBillingRequestId()
                }
                Label(attrs = { classes("bill-field") }) {
                    Text("Temporary password (only for reset)")
                    Input(org.jetbrains.compose.web.attributes.InputType.Password, attrs = {
                        value(password)
                        attr("autocomplete", "new-password")
                        if (busy) attr("disabled", "")
                        onInput { password = it.value }
                    })
                }
                listOf("LOCK", "UNLOCK", "REVOKE_SESSIONS", "RESET_PASSWORD").forEach { action ->
                    BillButton(action.replace('_', ' '), busy || reason.trim().length < 3) {
                        if (window.confirm("$action for ${user.username}? Existing sessions will be invalidated.")) {
                            run {
                                workbenchCall<JsonObject>(
                                    "/admin/accounts/${user.id}/action",
                                    "POST",
                                    workbenchJson.encodeToString(AdminAction(action, reason, password)),
                                )
                                password = ""
                                notice = "$action applied to ${user.username}."
                                reload(page)
                            }
                        }
                    }
                }
                P {
                    Text(
                        "Communicate temporary passwords through a secure channel. BILL does not email passwords or enforce a first-login password change.",
                    )
                }
                billing?.let { b ->
                    H3 {
                        Text(
                            "${b.access.plan} · ${b.access.remaining - b.access.addedCredits}/${b.access.limit} plan credits" +
                                if (b.access.addedCredits > 0) " + ${b.access.addedCredits} added" else "",
                        )
                    }
                    P { Text(b.access.description) }
                    P {
                        Text(
                            "Stripe customer: ${b.stripeCustomer.ifBlank {
                                "None"
                            }}. Complimentary access grants Pro features, not a Stripe discount. Active subscriptions or paid access must be adjusted in Stripe first; this control never cancels charges or issues refunds.",
                        )
                    }
                    BillField("Complimentary months (1–12)", months) {
                        months = it
                        grantId = newBillingRequestId()
                    }
                    BillButton("Grant complimentary access", busy || reason.trim().length < 3) {
                        if (window.confirm("Grant $months complimentary months to ${user.username}?")) {
                            run {
                                billing =
                                    workbenchCall(
                                        "/admin/billing/${user.id}/grants",
                                        "POST",
                                        workbenchJson.encodeToString(
                                            GrantRequest(
                                                grantId,
                                                months.toIntOrNull() ?: 0,
                                                reason,
                                            ),
                                        ),
                                    )
                                grantId = newBillingRequestId()
                                notice = "Complimentary access granted."
                            }
                        }
                    }
                    P {
                        Text(
                            "Added credits work on any plan, alongside a subscription or complimentary access. They are spent after the plan's credits and never reset. Each grant is recorded with its reason.",
                        )
                    }
                    BillField("Credits to add (1–${MAX_CREDIT_GRANT})", credits) {
                        credits = it
                        creditGrantId = newBillingRequestId()
                    }
                    val creditCount = credits.trim().toIntOrNull()
                    BillButton(
                        "Add credits",
                        busy || reason.trim().length < 3 || creditCount == null || creditCount !in 1..MAX_CREDIT_GRANT,
                    ) {
                        if (window.confirm("Add $creditCount drafting credits to ${user.username}?")) {
                            run {
                                billing =
                                    workbenchCall(
                                        "/admin/billing/${user.id}/credits",
                                        "POST",
                                        workbenchJson.encodeToString(CreditGrantRequest(creditGrantId, creditCount ?: 0, reason)),
                                    )
                                creditGrantId = newBillingRequestId()
                                credits = ""
                                notice = "Added $creditCount credits to ${user.username}."
                            }
                        }
                    }
                    b.creditGrants.forEach { grant ->
                        P { Text("+${grant.credits} credits · ${stamp(grant.at * 1000)} · by #${grant.actor} · ${grant.reason}") }
                    }
                    b.grants.forEach { grant ->
                        P {
                            Text(
                                "Grant through ${stamp(
                                    grant.until * 1000,
                                )} · ${if (grant.revoked) "Revoked" else "Not revoked"} · ${grant.reason}",
                            )
                        }
                        if (!grant.revoked) {
                            BillButton("Revoke grant ${grant.id.take(8)}", busy || reason.trim().length < 3) {
                                if (window.confirm("Revoke this complimentary access grant?")) {
                                    run {
                                        billing =
                                            workbenchCall(
                                                "/admin/billing/${user.id}/grants/${grant.id}/revoke",
                                                "POST",
                                                workbenchJson.encodeToString(AdminAction("REVOKE", reason)),
                                            )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        if (notice.isNotBlank()) P({ attr("role", "status") }) { Text(notice) }
    }
}

@Composable
internal fun SupportPanel(admin: Boolean = false) {
    var inbox by remember { mutableStateOf<SupportList?>(null) }
    var selected by remember { mutableStateOf<SupportTicket?>(null) }
    var subject by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var reply by remember { mutableStateOf("") }
    var status by remember { mutableStateOf(if (admin) "OPEN" else "") }
    var close by remember { mutableStateOf(false) }
    var page by remember { mutableStateOf(0) }
    var createId by remember { mutableStateOf(newBillingRequestId()) }
    var replyId by remember { mutableStateOf(newBillingRequestId()) }
    val prefix = if (admin) "/admin/support" else "/support"
    OperationForm { busy, run ->
        suspend fun reload(next: Int = 0) {
            inbox = workbenchCall("$prefix?status=$status&page=$next")
            page = next
        }
        LaunchedEffect(Unit) { run { reload() } }
        H2 { Text(if (admin) "Customer support inbox" else "Feedback & support") }
        P {
            Text(
                "Messages go to BILL administrators, not the AI. Do not include client confidences, billing notes, passwords, or payment-card details. Replies appear here; email notifications are not sent. Check back for a response.",
            )
        }
        BillSelect("Conversation filter", status, mapOf("" to "All", "OPEN" to "Open", "CLOSED" to "Closed")) { status = it }
        BillButton("Refresh conversations", busy) {
            run {
                reload()
                selected?.let { selected = workbenchCall("$prefix/${it.id}") }
            }
        }
        if (inbox?.tickets?.isEmpty() == true) P { Text("No conversations match this filter.") }
        inbox?.tickets?.forEach { ticket ->
            BillButton("${ticket.status}: ${ticket.subject}${if (admin) " · account #${ticket.owner}" else ""}", busy) {
                run {
                    selected = workbenchCall("$prefix/${ticket.id}")
                    reply = ""
                    close = false
                    replyId = newBillingRequestId()
                }
            }
        }
        BillButton("Previous conversations", busy || page == 0) { run { reload(page - 1) } }
        BillButton("Next conversations", busy || inbox?.more != true) { run { reload(page + 1) } }
        selected?.let { ticket ->
            Div({ classes("bill-card") }) {
                H3 { Text(ticket.subject) }
                P { Text("${ticket.status} · updated ${stamp(ticket.updatedAt)}") }
                ticket.messages.forEach { item ->
                    Div({ classes("bill-source") }) {
                        P { Text("${if (item.fromSupport) "BILL support" else "Customer"} · ${stamp(item.at)}") }
                        P { Text(item.body) }
                    }
                }
                BillArea("Your reply", reply) {
                    reply = it
                    replyId = newBillingRequestId()
                }
                if (admin) {
                    BillCheck("Close conversation after replying", close) {
                        close = it
                        replyId = newBillingRequestId()
                    }
                }
                BillButton("Send reply", busy || reply.trim().length < 3, "primary") {
                    run {
                        selected =
                            workbenchCall(
                                "$prefix/${ticket.id}/reply",
                                "POST",
                                workbenchJson.encodeToString(
                                    SupportReply(replyId, ticket.revision, reply, if (close) "CLOSED" else "OPEN"),
                                ),
                            )
                        reply = ""
                        replyId = newBillingRequestId()
                        reload()
                    }
                }
            }
        }
        if (!admin) {
            Div({ classes("bill-card") }) {
                H3 { Text("Start a conversation") }
                BillField("Subject", subject) {
                    subject = it
                    createId = newBillingRequestId()
                }
                BillArea("Feedback or question", message) {
                    message = it
                    createId = newBillingRequestId()
                }
                BillButton("Send feedback", busy || subject.trim().length < 3 || message.trim().length < 3, "primary") {
                    run {
                        selected =
                            workbenchCall("/support", "POST", workbenchJson.encodeToString(SupportCreate(createId, subject, message)))
                        subject = ""
                        message = ""
                        createId = newBillingRequestId()
                        reload()
                    }
                }
            }
        }
    }
}

@Composable
private fun OperationsAuditPanel() {
    var records by remember { mutableStateOf<List<AuditItem>>(emptyList()) }
    OperationForm { busy, run ->
        suspend fun reload() {
            records =
                (
                    workbenchCall<List<AuditItem>>(
                        "/admin/audit",
                    ) + workbenchCall<List<AuditItem>>("/admin/account-audit")
                ).sortedByDescending { it.at }
        }
        LaunchedEffect(Unit) { run { reload() } }
        H2 { Text("Administrative audit") }
        P {
            Text(
                "Latest 100 billing-policy/access actions and latest 100 account/support actions. Audit entries contain reasons and identifiers, not passwords or support message bodies.",
            )
        }
        BillButton("Refresh audit", busy) { run { reload() } }
        records.forEach { item ->
            Div({ classes("bill-card") }) {
                P { Text("${stamp(item.at)} · actor #${item.actor} · ${item.target} · ${item.action}") }
                P { Text(item.reason) }
            }
        }
    }
}

/** Mirrors the server's per-grant limit (BillingOperations.MAX_CREDIT_GRANT). */
private const val MAX_CREDIT_GRANT = 10_000
